import { lazy, Suspense } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { TrpcProvider } from "./lib/trpc-provider.js";
import { RequireAuth } from "./features/auth/RequireAuth.js";
import { AppShell } from "./components/AppShell.js";
import { Vazio } from "./components/ui.js";
import { NovaGeracaoPage } from "./features/geracao/NovaGeracaoPage.js";
import { ImpressaoPage } from "./features/impressao/ImpressaoPage.js";
import { MarcadoresPage } from "./features/marcadores/MarcadoresPage.js";
import { ModelosDeMarcadorPage } from "./features/marcadores/ModelosDeMarcadorPage.js";
import { FigurinhasPage } from "./features/figurinhas/FigurinhasPage.js";
import { BibliotecaPage } from "./features/biblioteca/BibliotecaPage.js";
import { LotesPage } from "./features/lotes/LotesPage.js";
import { NovoLotePage } from "./features/lotes/NovoLotePage.js";
import { LotePage } from "./features/lotes/LotePage.js";
import { KioskPage } from "./features/conta/KioskPage.js";
import { PacotePage } from "./features/conta/PacotePage.js";
import { ComprarPage } from "./features/conta/ComprarPage.js";
import { BancoImagensPage, CapasPage, DivulgacoesPage } from "./features/acervo/paginas.js";
import { MusicasPage } from "./features/acervo/MusicasPage.js";
import { InstituicoesPage, ParticipantesPage, VivenciasPage } from "./features/admin/paginas.js";
import { GaleriaPage } from "./features/galeria/GaleriaPage.js";
import { CadastroPage } from "./features/auth/CadastroPage.js";
import { RecuperarSenhaPage } from "./features/auth/RecuperarSenhaPage.js";
import { LegalPage } from "./features/landing/LegalPage.js";
import { LoginPage } from "./features/auth/LoginPage.js";
import { CustosPage } from "./features/admin/CustosPage.js";
import { PacotesPage } from "./features/admin/PacotesPage.js";
import { PagamentosPage } from "./features/admin/PagamentosPage.js";
import { UsuariosPage } from "./features/admin/UsuariosPage.js";
import { ConfiguracoesPage } from "./features/config/ConfiguracoesPage.js";

// A vitrine: a landing pública da raiz. Nasceu como "proposta 2", e é por isso que
// o plano em thoughts/plans/2026-08-21-1930-proposta2-landing.md a chama assim.
const Vitrine = lazy(() =>
  import("./features/landing/vitrine/Vitrine.js").then((modulo) => ({
    default: modulo.Vitrine,
  })),
);

// A landing que estava no ar até aqui. Mantida numa rota própria porque, nos
// primeiros dias, comparar as duas em produção vale mais do que economizar um
// arquivo — e porque voltar atrás fica a um `path` de distância.
const LandingAnterior = lazy(() =>
  import("./features/landing/LandingPage.js").then((modulo) => ({ default: modulo.LandingPage })),
);

/**
 * A rota inicial é `/nova` — a tela de atendimento.
 *
 * O sistema anterior abria num painel administrativo, o que fazia o operador
 * navegar antes de poder trabalhar. Aqui a primeira tela já é o trabalho.
 */
export function App() {
  return (
    <TrpcProvider>
      <Router>
        <Routes>
          {/* A galeria roda numa TV, sem login e fora da casca. */}
          <Route path="/galeria" element={<GaleriaPage />} />

          {/* Cadastro é para quem ainda não tem conta: fica fora do
              `RequireAuth`, que manda todo anônimo para a tela de entrada. */}
          <Route path="/cadastro" element={<CadastroPage />} />
          <Route path="/recuperar" element={<RecuperarSenhaPage />} />

          {/* A raiz é a vitrine, não o app: quem chega sem conta precisa
              entender o que é isto antes de ver uma tela de login. Quem já
              entrou é levado para o trabalho pela própria landing. */}
          <Route
            path="/"
            element={
              <Suspense fallback={<CarregandoLanding />}>
                <Vitrine />
              </Suspense>
            }
          />
          {/* O link de revisão que circulou não leva 404, e a raiz não passa a ter
              duas URLs com o mesmo conteúdo disputando indexação. */}
          <Route path="/proposta2" element={<Navigate to="/" replace />} />
          <Route
            path="/landing-anterior"
            element={
              <Suspense fallback={<CarregandoLanding />}>
                <LandingAnterior />
              </Suspense>
            }
          />
          <Route path="/privacidade" element={<LegalPage tipo="privacidade" />} />
          <Route path="/termos" element={<LegalPage tipo="termos" />} />
          <Route path="/politica-imagens-ia" element={<LegalPage tipo="imagens-ia" />} />
          <Route path="/entrar" element={<LoginPage />} />

          <Route
            path="*"
            element={
              <RequireAuth>
                <AppShell>
                  <Routes>
                    <Route path="/" element={<Navigate to="/nova" replace />} />
                    <Route
                      path="/nova"
                      element={
                        <RequireAuth area="atendimento">
                          <NovaGeracaoPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/biblioteca"
                      element={
                        <RequireAuth area="atendimento">
                          <BibliotecaPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/impressao"
                      element={
                        <RequireAuth area="atendimento">
                          <ImpressaoPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/marcadores"
                      element={
                        <RequireAuth area="atendimento">
                          <MarcadoresPage />
                        </RequireAuth>
                      }
                    />
                    {/* `/lotes/novo` antes de `/lotes/:loteId`: sem isso a
                        rota com parâmetro casaria com "novo" e tentaria abrir
                        um lote chamado assim. */}
                    <Route
                      path="/lotes"
                      element={
                        <RequireAuth area="atendimento">
                          <LotesPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/lotes/novo"
                      element={
                        <RequireAuth area="atendimento">
                          <NovoLotePage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/lotes/:loteId"
                      element={
                        <RequireAuth area="atendimento">
                          <LotePage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/pacote"
                      element={
                        <RequireAuth area="atendimento">
                          <PacotePage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/comprar"
                      element={
                        <RequireAuth area="atendimento">
                          <ComprarPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/kiosk"
                      element={
                        <RequireAuth area="atendimento">
                          <KioskPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/banco-imagens"
                      element={
                        <RequireAuth area="acervo">
                          <BancoImagensPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/banco-capas"
                      element={
                        <RequireAuth area="acervo">
                          <CapasPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/divulgacoes"
                      element={
                        <RequireAuth area="acervo">
                          <DivulgacoesPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/modelos-de-marcador"
                      element={
                        <RequireAuth area="acervo">
                          <ModelosDeMarcadorPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="/musicas"
                      element={
                        <RequireAuth area="acervo">
                          <MusicasPage />
                        </RequireAuth>
                      }
                    />
                    {/* Acervo, como as músicas e as capas: é um acervo da casa. Quem monta folhas na
                        Impressão (atendimento) entra aqui sem pedir nada: cliente, operador e admin têm
                        as duas áreas em `permissoes.ts`, e só o quiosque não tem nenhuma. */}
                    <Route
                      path="/figurinhas"
                      element={
                        <RequireAuth area="acervo">
                          <FigurinhasPage />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/admin/*"
                      element={
                        <RequireAuth area="administracao">
                          <Routes>
                            <Route path="vivencias" element={<VivenciasPage />} />
                            <Route path="instituicoes" element={<InstituicoesPage />} />
                            <Route path="participantes" element={<ParticipantesPage />} />
                            <Route path="pacotes" element={<PacotesPage />} />
                            <Route path="pagamentos" element={<PagamentosPage />} />
                            <Route path="custos" element={<CustosPage />} />
                            <Route path="usuarios" element={<UsuariosPage />} />
                            <Route path="config" element={<ConfiguracoesPage />} />
                            <Route path="config/:secao" element={<ConfiguracoesPage />} />
                          </Routes>
                        </RequireAuth>
                      }
                    />

                    <Route path="*" element={<NaoEncontrada />} />
                  </Routes>
                </AppShell>
              </RequireAuth>
            }
          />
        </Routes>
      </Router>
    </TrpcProvider>
  );
}

function CarregandoLanding() {
  return (
    <div
      className="grid min-h-[100dvh] place-items-center bg-[#f7f2e8] text-sm text-[#14583e]"
      aria-live="polite"
      aria-busy="true"
    >
      Carregando página
    </div>
  );
}

function NaoEncontrada() {
  return (
    <Vazio
      titulo="Não achamos esta página"
      descricao="O endereço pode ter mudado. Use o menu para voltar ao atendimento."
    />
  );
}
